import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Linkedin, Mail, LifeBuoy, Phone, MessageCircle, MapPin, Layers } from "lucide-react";

const CONTACT_EMAIL = "contact@trinitylayers.in";
const SUPPORT_EMAIL = "support@trinitylayers.in";
const CONTACT_PHONE = "+917071441999";
const CONTACT_PHONE_DISPLAY = "+91 70714 41999";
const WHATSAPP_URL = `https://wa.me/${CONTACT_PHONE.replace(/[^0-9]/g, "")}`;

const linkCols = [
  { title: "Company", testId: "footer-col-company", links: [
    ["Contact Us", "/contact", "footer-contact-link"],
    ["Become an Expert", "/join/expert", null],
    ["Become a 3D Printer Owner", "/join/partner", null],
  ]},
  { title: "Experts", testId: "footer-col-experts", links: [
    ["3D Designers", "/experts?category=Designer", null],
    ["3D Printer Owners", "/experts/printing-partners", null],
    ["Engineers", "/experts?category=Engineer", null],
    ["Consultants", "/experts?category=Consultant", null],
  ]},
  { title: "Shop", testId: "footer-col-shop", links: [
    ["E-Files", "/shop/e-files", null],
    ["3D Printed Products", "/shop/printed-products", null],
    ["3D Printers", "/shop/printers", null],
    ["Filaments", "/shop/filaments", null],
    ["Resins", "/shop/resins", null],
    ["Equipment", "/shop/equipment", null],
  ]},
  { title: "Resources", testId: "footer-col-resources", links: [
    ["Blog", "/resources/blog", null],
    ["Guides", "/resources/guides", null],
    ["Tutorials", "/resources/tutorials", null],
    ["FAQs", "/resources/faqs", null],
  ]},
  { title: "Legal", testId: "footer-col-legal", links: [
    ["Terms", "/legal/terms", null],
    ["Privacy", "/legal/privacy", null],
    ["Refund Policy", "/legal/refund-policy", null],
    ["Seller Policy", "/legal/seller-policy", null],
  ]},
];

function ContactLine({ icon: Icon, href, label, testId, external }) {
  const props = external ? { target: "_blank", rel: "noopener noreferrer" } : {};
  return (
    <a href={href} data-testid={testId} {...props} className="flex items-center gap-2 text-xs text-[#475569] hover:text-slate-900 transition">
      <Icon className="w-3.5 h-3.5 shrink-0 text-[#0891B2]" />
      <span className="truncate">{label}</span>
    </a>
  );
}

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative border-t border-slate-200 bg-[#F1F5F9]">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#2563EB]/50 to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="mb-12 max-w-md">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#0891B2] flex items-center justify-center shadow-[0_4px_16px_rgba(37,99,235,0.35)]">
              <Layers className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display font-bold text-lg tracking-tight">Trinity Layers</span>
          </div>
          <p className="text-sm text-[#64748B] leading-relaxed">The premium marketplace for 3D design, digital files and on-demand manufacturing — from idea to finished part.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 mb-12">
          {linkCols.map((col) => (
            <div key={col.title} data-testid={col.testId}>
              <div className="tl-label mb-3">{col.title}</div>
              <ul className="flex flex-col gap-2">
                {col.links.map(([label, href, testId]) => (
                  <li key={label}>
                    <Link to={href} data-testid={testId || undefined} className="text-xs text-[#475569] hover:text-slate-900 transition">{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div data-testid="footer-col-contact">
            <div className="tl-label mb-3">Contact</div>
            <ul className="flex flex-col gap-2">
              <li><Link to="/contact" data-testid="footer-contact-us-link" className="text-xs text-[#475569] hover:text-slate-900 transition">Contact Us</Link></li>
              <li><ContactLine icon={Mail} href={`mailto:${CONTACT_EMAIL}`} label={CONTACT_EMAIL} testId="footer-email-general" /></li>
              <li><ContactLine icon={LifeBuoy} href={`mailto:${SUPPORT_EMAIL}`} label={SUPPORT_EMAIL} testId="footer-email-support" /></li>
              <li><ContactLine icon={Phone} href={`tel:${CONTACT_PHONE}`} label={CONTACT_PHONE_DISPLAY} testId="footer-phone" /></li>
              <li><ContactLine icon={MessageCircle} href={WHATSAPP_URL} label="WhatsApp" testId="footer-whatsapp" external /></li>
              <li className="flex items-start gap-2 text-xs text-[#64748B] mt-1">
                <MapPin className="w-3.5 h-3.5 shrink-0 text-[#0891B2] mt-0.5" />
                <span>76/A Nijamuddinpura,<br />MAU, Uttar Pradesh,<br />275101, India</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-slate-200/60 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-2 text-[#475569]">
            <Layers className="w-4 h-4 text-[#0891B2]" />
            <span className="font-display font-semibold text-sm">Trinity Layers</span>
          </div>
          <div className="flex items-center gap-3">
            <a data-testid="footer-social-instagram" href="https://www.instagram.com/trinitylayer/" target="_blank" rel="noopener noreferrer" aria-label="Trinity Layers on Instagram" className="w-9 h-9 rounded-full border border-slate-200 bg-slate-50 flex items-center justify-center text-[#475569] hover:text-[#0891B2] hover:border-[#0891B2]/50 hover:bg-[#0891B2]/10 transition">
              <Instagram className="w-4 h-4" />
            </a>
            <a data-testid="footer-social-linkedin" href="https://www.linkedin.com/company/trinitylayers/" target="_blank" rel="noopener noreferrer" aria-label="Trinity Layers on LinkedIn" className="w-9 h-9 rounded-full border border-slate-200 bg-slate-50 flex items-center justify-center text-[#475569] hover:text-[#0891B2] hover:border-[#0891B2]/50 hover:bg-[#0891B2]/10 transition">
              <Linkedin className="w-4 h-4" />
            </a>
          </div>
          <div className="text-xs text-[#64748B]">© 2026 Trinity Layers. All rights reserved.</div>
        </div>
      </div>
    </footer>
  );
}
